import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { trackEventAsync, setUserId } from 'sh-www/utils/datalayer';
import { task } from 'ember-concurrency';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { USER } from 'sh-www/models/user';
import { blockingLocationAssign } from 'sh-www/utils/window';

class SignupCredentialsController extends Controller {
  queryParams = [
    'email',
    'ref',
    'full_name',
    'friend',
    'company',
    /*'utm_campaign', 'utm_content', 'utm_medium', 'utm_source', 'utm_term'*/
  ];

  @service auth;
  @service media;
  @service router;
  @service session;
  @service store;
  @service('local-storage-fallback') storage;
  @service('commands/user-commands') userCommandService;
  @service('error-tracker') errorTracker;

  @tracked email = null;
  @tracked ref = null;
  @tracked full_name = null;
  @tracked friend = null;
  @tracked company = null;

  get chosenEmail() {
    if (this.session.isAuthenticated) {
      return this.session.currentUser.email;
    }
    return this.email;
  }

  get chosenFullName() {
    if (this.session.isAuthenticated) {
      return this.session.currentUser.fullName;
    }
    return this.full_name;
  }

  get title() {
    if (this.friend && this.company) {
      return `Join ${this.friend} and ${this.company} at Glimmer`;
    }

    if (this.friend) {
      return `Join ${this.friend} at Glimmer`;
    }

    return 'Sign up as a Creator';
  }

  get subtitle() {
    return this.friend
      ? ''
      : `Looking to hire creators? <a href="/brand/signup/" target="_blank">Sign up as a brand</a>`;
  }

  /**
   * @returns {*}
   */
  @task
  *onAuthSuccess() {
    setUserId(this, this.session.currentUser.id);
    yield trackEventAsync(this, 'sign_up');

    this.storage.setItem('creatorSignup', true);
    yield blockingLocationAssign('/start');
  }

  @task
  *onGoogleSSO(credential) {
    try {
      if (!this.session.isAuthenticated) {
        yield this.auth.signupGoogleJwt(credential, USER.TYPES.SELLER);
      } else {
        yield this.auth.loginGoogleJwt(credential);
      }
    } catch (e) {
      this.errorTracker.warn('Google authentication failed', this, e);
      handleErrorResponseWithModal(this, e);
      throw e;
    }

    yield this.onAuthSuccess.perform();
  }

  @task
  *onSignupForm({ full_name, email, password }) {
    // if user already has an account due to an invite just update them
    if (this.session.isAuthenticated) {
      yield this.userCommandService.updateUser(this.session.currentUser, { full_name, legal_accept: 1 });

      // invited users that choose 'forgot password' already have this set
      if (!this.session.currentUser.hasPassword) {
        yield this.auth.updatePassword(email, password, password);
      }
    } else {
      yield this.auth.createUser({ full_name, email, password, type: USER.TYPES.SELLER });
    }

    yield this.onAuthSuccess.perform();
  }
}

export default SignupCredentialsController;
